/**
 * Stylized target / silhouette with numbered bullet holes for the
 * current round's shots. Lives in @flm9/stream-client because both
 * the referee app (manual scoring, fed from the offline DB) and the
 * board app (latest server-fetched shots) render it as a kiosk-side
 * visualization.
 *
 * Render path is chosen by the input data:
 *   - real `targetZones` geometry that isn't all concentric circles
 *     → generic shape renderer (faithful to the admin-defined target).
 *   - `ring_value` schema with concentric or no geometry
 *     → polished concentric-rings renderer with value labels.
 *   - `hit_factor` schema with no geometry
 *     → hardcoded canonical IPSC silhouette.
 *
 * Shot markers come from one of two paths:
 *   - `shotPositions` carries template-local (x, y) for shots that
 *     were recorded from a CV detection (joined via
 *     `vision_detections.consumed_shot_id`). Plotted at their real
 *     coordinates inside the same coord system as `targetZones`.
 *   - Otherwise the marker falls back to zone-centroid + radial
 *     fan among shots sharing a zoneCode, capped by zone inradius.
 *
 * `pendingDetections` are CV detections that haven't been confirmed
 * by the referee yet — they render as dashed translucent ghosts
 * with their own click handlers so the referee can übernehmen /
 * verwerfen them inline.
 */
export type HitSchemaKind = 'ring_value' | 'hit_factor';

export type PenaltyCode = 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';

export interface HitMarker {
  id: string;
  ord: number;
  valueNumeric: number | null;
  zoneCode: string | null;
}

export interface HitZone {
  zoneCode: string;
  valueNumeric: number;
  ordering: number;
  displayColor: string | null;
}

/**
 * Geometric definition of a hit zone on the target template image,
 * matched against `HitZone` by `zoneCode`. Coordinates are in
 * template-local units where x AND y are fractions of image WIDTH;
 * height extent is `imageHeightPx / imageWidthPx`.
 */
export type TargetZoneGeometry =
  | { shape: 'circle'; geometry: { cx: number; cy: number; r: number } }
  | { shape: 'rect'; geometry: { x: number; y: number; w: number; h: number } }
  | { shape: 'polygon'; geometry: { points: [number, number][] } };

export type TargetZone = {
  zoneCode: string;
  ordering: number;
  displayColor?: string | null;
} & TargetZoneGeometry;

/**
 * Real (x, y) coordinates for a shot, carried in template-local
 * width-units (same coord system as `TargetZone.geometry`).
 * Sourced from a confirmed vision detection.
 */
export interface ShotPosition {
  shotId: string;
  x: number;
  y: number;
  estimated?: boolean;
}

/**
 * A vision detection that hasn't been confirmed (or dismissed) by
 * the referee yet. Rendered as a dashed translucent marker; tapping
 * it fires `onConfirmDetection`.
 */
export interface PendingDetection {
  id: string;
  /** Template-local x in width-units. */
  x: number;
  /** Template-local y in width-units. */
  y: number;
  valueNumeric: number | null;
  zoneCode: string | null;
  confidence: number | null;
}

export interface PenaltyEntry {
  id: string;
  code: PenaltyCode;
  points: number;
}

interface Props {
  shots: HitMarker[];
  shotCount: number;
  schemaKind: HitSchemaKind;
  /**
   * Zones for this schema, ordered inside-out (lowest `ordering`
   * first = innermost = highest value). Drives ring count + values
   * for ring_value layouts; for hit_factor we fall back to the
   * canonical IPSC silhouette when no `targetZones` are provided.
   */
  zones: HitZone[];
  /**
   * Optional zone geometry for the target. When provided AND the
   * shapes aren't all concentric circles, the indicator renders the
   * actual shapes instead of synthesised concentric rings — so a
   * square steel plate looks like a square, an IPSC silhouette uses
   * its real polygon outline, etc.
   */
  targetZones?: TargetZone[] | undefined;
  /**
   * Real coordinates for shots that came from a CV detection.
   * Lookup key: shot id. Coords are template-local width-units.
   */
  shotPositions?: ShotPosition[] | undefined;
  /**
   * CV detections awaiting referee confirmation. Rendered as ghost
   * markers; tapping confirms via `onConfirmDetection`.
   */
  pendingDetections?: PendingDetection[] | undefined;
  /** Round-level penalties for this assignment, rendered as badges. */
  penalties?: PenaltyEntry[] | undefined;
  /** Referee tapped a ghost marker — accept it as the next shot. */
  onConfirmDetection?: ((detectionId: string) => void) | undefined;
  /** Referee long-pressed (or right-clicked) a ghost marker — dismiss. */
  onDismissDetection?: ((detectionId: string) => void) | undefined;
}

export function HitIndicator(props: Props) {
  const estimated = props.shotPositions?.some((p) => p.estimated);
  return (
    <>
      <HitIndicatorTarget {...props} />
      {estimated && (
        <p className="mt-1 text-xs text-amber-300">
          Schusszuordnung geschätzt — Reihenfolge aus dem Endbild nicht erkennbar.
        </p>
      )}
    </>
  );
}

function HitIndicatorTarget({
  shots,
  shotCount,
  schemaKind,
  zones,
  targetZones,
  shotPositions,
  pendingDetections,
  penalties,
  onConfirmDetection,
  onDismissDetection,
}: Props) {
  const sortedTargetZones = targetZones
    ? [...targetZones].sort((a, b) => a.ordering - b.ordering)
    : null;
  const positionByShotId = new Map((shotPositions ?? []).map((p) => [p.shotId, p]));

  // When real geometry is provided AND it isn't a concentric-circle
  // target, use the generic shape renderer regardless of schema kind.
  if (
    sortedTargetZones &&
    sortedTargetZones.length > 0 &&
    !areConcentricCircles(sortedTargetZones)
  ) {
    return (
      <GenericShapeTarget
        shots={shots}
        shotCount={shotCount}
        schemaKind={schemaKind}
        zones={zones}
        targetZones={sortedTargetZones}
        positionByShotId={positionByShotId}
        pendingDetections={pendingDetections ?? []}
        penalties={penalties ?? []}
        onConfirmDetection={onConfirmDetection}
        onDismissDetection={onDismissDetection}
      />
    );
  }

  if (schemaKind === 'hit_factor') {
    return (
      <IpscHitTarget
        shots={shots}
        shotCount={shotCount}
        zones={zones}
        penalties={penalties ?? []}
      />
    );
  }
  // ring_value (concentric or no geometry). Sort the zones inside-out
  // and pull the numeric values out for ring math. Falls back to the
  // legacy 11-ring set when a schema arrives without zones.
  const ordered = [...zones].sort((a, b) => a.ordering - b.ordering);
  const ringValues =
    ordered.length > 0 ? ordered.map((z) => z.valueNumeric) : [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0];
  const colorByValue = new Map(ordered.map((z) => [z.valueNumeric, z.displayColor]));
  // For the concentric-rings case, infer a (cx, cy, r_outer) so we
  // can map template-local CV coords onto the synthesised viewBox.
  const concentricBounds =
    sortedTargetZones && sortedTargetZones.length > 0
      ? concentricCircleBounds(sortedTargetZones)
      : null;
  return (
    <PrecisionHitTarget
      shots={shots}
      shotCount={shotCount}
      ringValues={ringValues}
      colorByValue={colorByValue}
      positionByShotId={positionByShotId}
      pendingDetections={pendingDetections ?? []}
      penalties={penalties ?? []}
      concentricBounds={concentricBounds}
      onConfirmDetection={onConfirmDetection}
      onDismissDetection={onDismissDetection}
    />
  );
}

const VIEW = 220;
const CENTER = VIEW / 2;
const MAX_R = 100;

function ringFill(
  value: number,
  ringValues: number[],
  colorByValue: Map<number, string | null>,
): string {
  // Honour admin-set zone colour when present — keeps the editor's
  // colour swatches aligned with the on-target rendering.
  const admin = colorByValue.get(value);
  if (admin) return admin;
  // Heuristic fallback for un-coloured zones: 11-ring ÖSB style for
  // long lists, contrast bands for short lists.
  if (ringValues.length === 11) {
    if (value === 0) return '#1f1f23';
    if (value >= 7) return '#0a0a0a';
    return '#e5e7eb';
  }
  switch (value) {
    case 10:
      return '#0a0a0a';
    case 5:
      return '#3f3f46';
    case 1:
      return '#a1a1aa';
    default:
      return '#1f1f23';
  }
}

function ringLabelColor(value: number, ringValues: number[]): string {
  if (ringValues.length === 11) {
    if (value >= 7) return '#a1a1aa';
    return '#52525b';
  }
  return value === 10 || value === 5 ? '#a1a1aa' : '#52525b';
}

function holeFill(value: number | null): string {
  if (value == null) return '#71717a';
  if (value >= 9) return '#10b981';
  if (value >= 7) return '#84cc16';
  if (value >= 5) return '#eab308';
  if (value >= 3) return '#f97316';
  return '#ef4444';
}

function ipscHoleFill(zone: string | null, zoneColor: Map<string, string | null>): string {
  if (zone) {
    const c = zoneColor.get(zone);
    if (c) return c;
  }
  switch (zone) {
    case 'A':
      return '#10b981';
    case 'C':
      return '#84cc16';
    case 'D':
      return '#eab308';
    case 'M':
    case 'NS':
      return '#ef4444';
    default:
      return '#71717a';
  }
}

const PENALTY_HATCH_ID = 'hit-indicator-penalty-hatch';

function PenaltyHatchDef() {
  return (
    <defs>
      <pattern
        id={PENALTY_HATCH_ID}
        width={4}
        height={4}
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect width={4} height={4} fill="#7f1d1d" />
        <line x1={0} y1={0} x2={0} y2={4} stroke="#fca5a5" strokeWidth={1.2} />
      </pattern>
    </defs>
  );
}

const PENALTY_LABEL: Record<PenaltyCode, string> = {
  procedural: 'PE',
  miss: 'M',
  no_shoot: 'NS',
  extra_shot: 'XS',
};

function PenaltyBadges({
  penalties,
  x,
  y,
  rowW,
  badgeH,
  fontSize,
}: {
  penalties: PenaltyEntry[];
  /** Left edge of the badge row. */
  x: number;
  /** Top edge of the badge row. */
  y: number;
  /** Available width for the row. */
  rowW: number;
  badgeH: number;
  fontSize: number;
}) {
  if (penalties.length === 0) return null;
  const badgeW = Math.min(rowW / Math.max(penalties.length, 1), badgeH * 2.6);
  const totalW = badgeW * penalties.length;
  const startX = x + (rowW - totalW) / 2;
  return (
    <g>
      {penalties.map((p, i) => {
        const bx = startX + i * badgeW;
        return (
          <g key={p.id}>
            <rect
              x={bx + badgeW * 0.05}
              y={y}
              width={badgeW * 0.9}
              height={badgeH}
              rx={badgeH * 0.25}
              fill="#7f1d1d"
              stroke="#fecaca"
              strokeWidth={badgeH * 0.04}
            />
            <text
              x={bx + badgeW / 2}
              y={y + badgeH * 0.5}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={fontSize}
              fontWeight={800}
              fill="#fee2e2"
            >
              {PENALTY_LABEL[p.code]} {p.points}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function PendingMarker({
  cx,
  cy,
  r,
  fill,
  strokeW,
  fontSize,
  label,
  onConfirm,
  onDismiss,
}: {
  cx: number;
  cy: number;
  r: number;
  fill: string;
  strokeW: number;
  fontSize: number;
  label: string;
  onConfirm: (() => void) | undefined;
  onDismiss: (() => void) | undefined;
}) {
  const interactive = onConfirm != null;
  return (
    <g
      style={interactive ? { cursor: 'pointer' } : undefined}
      onClick={onConfirm ? () => onConfirm() : undefined}
      onContextMenu={
        onDismiss
          ? (e) => {
              e.preventDefault();
              onDismiss();
            }
          : undefined
      }
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={fill}
        fillOpacity={0.45}
        stroke="#0a0a0a"
        strokeOpacity={0.8}
        strokeWidth={strokeW * 2}
        strokeDasharray={`${r * 0.45} ${r * 0.3}`}
      />
      <text
        x={cx}
        y={cy + r * 0.05}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight={700}
        fill="#0a0a0a"
        fillOpacity={0.8}
      >
        {label}
      </text>
    </g>
  );
}

function PrecisionHitTarget({
  shots,
  shotCount,
  ringValues,
  colorByValue,
  positionByShotId,
  pendingDetections,
  penalties,
  concentricBounds,
  onConfirmDetection,
  onDismissDetection,
}: {
  shots: HitMarker[];
  shotCount: number;
  ringValues: number[];
  colorByValue: Map<number, string | null>;
  positionByShotId: Map<string, ShotPosition>;
  pendingDetections: PendingDetection[];
  penalties: PenaltyEntry[];
  /** Bounds of the real concentric-circle target in template units, when known. */
  concentricBounds: { cx: number; cy: number; r: number } | null;
  onConfirmDetection: ((detectionId: string) => void) | undefined;
  onDismissDetection: ((detectionId: string) => void) | undefined;
}) {
  const ringWidth = MAX_R / ringValues.length;
  // Outer→inner order so smaller rings draw on top.
  const outerToInner = [...ringValues].reverse();

  // Maps a template-local (x, y) onto the synthesised ring viewBox
  // by re-projecting through the real outer-ring bounds. Only used
  // when we know the real centre+radius from `concentricBounds`.
  const projectTemplateXY = (x: number, y: number): { cx: number; cy: number } => {
    if (!concentricBounds) return { cx: CENTER, cy: CENTER };
    const dx = (x - concentricBounds.cx) / concentricBounds.r;
    const dy = (y - concentricBounds.cy) / concentricBounds.r;
    return { cx: CENTER + dx * MAX_R, cy: CENTER + dy * MAX_R };
  };

  const markerR = 7.5;
  const strokeW = 0.4;

  return (
    <figure className="mx-auto w-full max-w-[16rem]">
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW + 24}`}
        className="w-full"
        role="img"
        aria-label="Trefferanzeige"
      >
        <PenaltyHatchDef />
        {/* Rings (outer first so inner draws on top) */}
        {outerToInner.map((v, i) => {
          const r = (ringValues.length - i) * ringWidth;
          const isPenalty = v < 0;
          return (
            <circle
              key={v}
              cx={CENTER}
              cy={CENTER}
              r={r}
              fill={isPenalty ? `url(#${PENALTY_HATCH_ID})` : ringFill(v, ringValues, colorByValue)}
              stroke="#52525b"
              strokeWidth={strokeW}
            />
          );
        })}

        {/* Ring labels (top half, just inside each ring boundary) */}
        {ringValues
          .filter((v) => v !== 0)
          .map((v) => {
            const idx = ringValues.indexOf(v);
            const labelR = idx * ringWidth + ringWidth * 0.5;
            return (
              <text
                key={`label-${v}`}
                x={CENTER}
                y={CENTER - labelR + 0.5}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={ringValues.length === 11 ? 4 : 6}
                fontWeight={600}
                fill={ringLabelColor(v, ringValues)}
              >
                {v}
              </text>
            );
          })}

        {/* Bullseye marker */}
        <circle cx={CENTER} cy={CENTER} r={0.8} fill="#fafafa" opacity={0.7} />

        {/* Pending CV detections — only useful when we can map their
            template coords onto this synthesised viewBox. */}
        {concentricBounds &&
          pendingDetections.map((d) => {
            const { cx, cy } = projectTemplateXY(d.x, d.y);
            const isPenalty = (d.valueNumeric ?? 0) < 0;
            return (
              <PendingMarker
                key={d.id}
                cx={cx}
                cy={cy}
                r={markerR}
                fill={isPenalty ? '#ef4444' : holeFill(d.valueNumeric)}
                strokeW={strokeW}
                fontSize={8}
                label={d.valueNumeric != null ? String(d.valueNumeric) : '?'}
                onConfirm={onConfirmDetection ? () => onConfirmDetection(d.id) : undefined}
                onDismiss={onDismissDetection ? () => onDismissDetection(d.id) : undefined}
              />
            );
          })}

        {/* Bullet holes — numbered in shot order */}
        {shots.map((s) => {
          const value = s.valueNumeric;
          const realPos = positionByShotId.get(s.id);
          let cx: number;
          let cy: number;
          if (realPos && concentricBounds) {
            const p = projectTemplateXY(realPos.x, realPos.y);
            cx = p.cx;
            cy = p.cy;
          } else {
            const idx = value != null ? ringValues.indexOf(value) : -1;
            let r: number;
            if (idx < 0) {
              r = MAX_R + ringWidth * 0.5;
            } else if (value === ringValues[0]) {
              r = ringWidth * 0.35;
            } else {
              r = idx * ringWidth + ringWidth * 0.5;
            }
            const slots = Math.max(shotCount, 1);
            const angle = ((s.ord - 1) / slots) * 2 * Math.PI - Math.PI / 2;
            cx = CENTER + r * Math.cos(angle);
            cy = CENTER + r * Math.sin(angle);
          }
          const isPenalty = value != null && value < 0;
          return (
            <g key={s.id}>
              <circle
                cx={cx}
                cy={cy}
                r={markerR}
                fill={isPenalty ? '#ef4444' : holeFill(value)}
                stroke="#0a0a0a"
                strokeWidth={1.2}
              />
              <text
                x={cx}
                y={cy + 0.5}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={8}
                fontWeight={800}
                fill="#0a0a0a"
              >
                {isPenalty ? '!' : s.ord}
              </text>
            </g>
          );
        })}

        <PenaltyBadges
          penalties={penalties}
          x={4}
          y={VIEW + 4}
          rowW={VIEW - 8}
          badgeH={16}
          fontSize={9}
        />
      </svg>
      <figcaption className="sr-only">
        Treffer in Reihenfolge auf der Zielscheibe; Zahl auf der Markierung = Schussnummer.
      </figcaption>
    </figure>
  );
}

const IPSC_VIEW_W = 200;
const IPSC_VIEW_H = 260;

interface IpscZoneShape {
  head: { cx: number; cy: number; rx: number; ry: number };
  body: { x: number; y: number; w: number; h: number; r: number };
  fill: string;
}

const IPSC_SHAPES: IpscZoneShape[] = [
  {
    head: { cx: 100, cy: 44, rx: 32, ry: 36 },
    body: { x: 32, y: 80, w: 136, h: 160, r: 8 },
    fill: '#854d0e',
  },
  {
    head: { cx: 100, cy: 44, rx: 22, ry: 26 },
    body: { x: 48, y: 92, w: 104, h: 130, r: 6 },
    fill: '#a16207',
  },
  {
    head: { cx: 100, cy: 44, rx: 12, ry: 18 },
    body: { x: 76, y: 100, w: 48, h: 80, r: 3 },
    fill: '#15803d',
  },
];

function ipscShotPos(zone: string | null, ord: number, shotCount: number) {
  const slots = Math.max(shotCount, 1);
  const angle = ((ord - 1) / slots) * 2 * Math.PI - Math.PI / 2;
  switch (zone) {
    case 'A': {
      const cx = 100 + 12 * Math.cos(angle);
      const cy = 138 + 28 * Math.sin(angle);
      return { cx, cy };
    }
    case 'C': {
      const cx = 100 + 38 * Math.cos(angle);
      const cy = 142 + 48 * Math.sin(angle);
      return { cx, cy };
    }
    case 'D': {
      const cx = 100 + 56 * Math.cos(angle);
      const cy = 180 + 50 * Math.sin(angle);
      return { cx, cy };
    }
    case 'M': {
      const row = Math.floor((ord - 1) / 4);
      const col = (ord - 1) % 4;
      return { cx: 12 + col * 5, cy: 245 - row * 18 };
    }
    case 'NS': {
      const row = Math.floor((ord - 1) / 4);
      const col = (ord - 1) % 4;
      return { cx: 188 - col * 5, cy: 245 - row * 18 };
    }
    default:
      return { cx: 100, cy: 250 };
  }
}

function IpscHitTarget({
  shots,
  shotCount,
  zones,
  penalties,
}: {
  shots: HitMarker[];
  shotCount: number;
  zones: HitZone[];
  penalties: PenaltyEntry[];
}) {
  const zoneColor = new Map<string, string | null>(zones.map((z) => [z.zoneCode, z.displayColor]));
  return (
    <figure className="mx-auto w-full max-w-[14rem]">
      <svg
        viewBox={`0 0 ${IPSC_VIEW_W} ${IPSC_VIEW_H + 24}`}
        className="w-full"
        role="img"
        aria-label="Trefferanzeige IPSC-Silhouette"
      >
        <PenaltyHatchDef />
        {IPSC_SHAPES.map((shape, idx) => (
          <g key={idx}>
            <ellipse
              cx={shape.head.cx}
              cy={shape.head.cy}
              rx={shape.head.rx}
              ry={shape.head.ry}
              fill={shape.fill}
              stroke="#3f3f46"
              strokeWidth={0.6}
            />
            <rect
              x={shape.body.x}
              y={shape.body.y}
              width={shape.body.w}
              height={shape.body.h}
              rx={shape.body.r}
              fill={shape.fill}
              stroke="#3f3f46"
              strokeWidth={0.6}
            />
          </g>
        ))}

        <text x={100} y={48} textAnchor="middle" fontSize={6} fontWeight={700} fill="#bbf7d0">
          A
        </text>
        <text x={100} y={142} textAnchor="middle" fontSize={9} fontWeight={700} fill="#bbf7d0">
          A
        </text>
        <text x={56} y={108} fontSize={7} fontWeight={700} fill="#fef3c7">
          C
        </text>
        <text x={40} y={232} fontSize={7} fontWeight={700} fill="#fde68a">
          D
        </text>

        {shots.map((s) => {
          const { cx, cy } = ipscShotPos(s.zoneCode, s.ord, shotCount);
          return (
            <g key={s.id}>
              <circle
                cx={cx}
                cy={cy}
                r={7.5}
                fill={ipscHoleFill(s.zoneCode, zoneColor)}
                stroke="#0a0a0a"
                strokeWidth={1.2}
              />
              <text
                x={cx}
                y={cy + 0.5}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={8}
                fontWeight={800}
                fill="#0a0a0a"
              >
                {s.ord}
              </text>
            </g>
          );
        })}

        <PenaltyBadges
          penalties={penalties}
          x={4}
          y={IPSC_VIEW_H + 4}
          rowW={IPSC_VIEW_W - 8}
          badgeH={16}
          fontSize={9}
        />
      </svg>
      <figcaption className="sr-only">
        Treffer in Reihenfolge auf der IPSC-Silhouette; Zahl auf der Markierung = Schussnummer.
      </figcaption>
    </figure>
  );
}

const EPS = 1e-3;

/**
 * True when every zone is a circle sharing the same centre (i.e. a
 * classic precision/ring target). The polished concentric-rings
 * renderer handles this case better than the generic one — it
 * adds value labels, bullseye dot, and even ring spacing — so we
 * still route to it when the data shape allows.
 */
function areConcentricCircles(zones: TargetZone[]): boolean {
  if (zones.length === 0) return false;
  const circles = zones.filter((z): z is TargetZone & { shape: 'circle' } => z.shape === 'circle');
  if (circles.length !== zones.length) return false;
  const first = circles[0];
  if (!first) return false;
  const { cx, cy } = first.geometry;
  return circles.every(
    (z) => Math.abs(z.geometry.cx - cx) < EPS && Math.abs(z.geometry.cy - cy) < EPS,
  );
}

/**
 * Returns the centre + outer radius of a concentric-circle target in
 * template-local units, used to map CV-detected coordinates onto the
 * synthesised ring viewBox. Caller has already verified that the
 * zones are concentric circles.
 */
function concentricCircleBounds(zones: TargetZone[]): { cx: number; cy: number; r: number } | null {
  const circles = zones.filter((z): z is TargetZone & { shape: 'circle' } => z.shape === 'circle');
  const first = circles[0];
  if (!first) return null;
  const r = Math.max(...circles.map((c) => c.geometry.r));
  return { cx: first.geometry.cx, cy: first.geometry.cy, r };
}

function zoneBounds(z: TargetZone): { minX: number; minY: number; maxX: number; maxY: number } {
  switch (z.shape) {
    case 'circle': {
      const { cx, cy, r } = z.geometry;
      return { minX: cx - r, minY: cy - r, maxX: cx + r, maxY: cy + r };
    }
    case 'rect': {
      const { x, y, w, h } = z.geometry;
      return { minX: x, minY: y, maxX: x + w, maxY: y + h };
    }
    case 'polygon': {
      const xs = z.geometry.points.map((p) => p[0]);
      const ys = z.geometry.points.map((p) => p[1]);
      return {
        minX: Math.min(...xs),
        minY: Math.min(...ys),
        maxX: Math.max(...xs),
        maxY: Math.max(...ys),
      };
    }
  }
}

function zoneCentroid(z: TargetZone): { cx: number; cy: number } {
  switch (z.shape) {
    case 'circle':
      return { cx: z.geometry.cx, cy: z.geometry.cy };
    case 'rect':
      return { cx: z.geometry.x + z.geometry.w / 2, cy: z.geometry.y + z.geometry.h / 2 };
    case 'polygon': {
      // Geometric centroid via the shoelace formula. Falls back to
      // the bounding-box centre for degenerate (collinear) inputs.
      const pts = z.geometry.points;
      let area = 0;
      let cx = 0;
      let cy = 0;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        if (!a || !b) continue;
        const [x0, y0] = a;
        const [x1, y1] = b;
        const cross = x0 * y1 - x1 * y0;
        area += cross;
        cx += (x0 + x1) * cross;
        cy += (y0 + y1) * cross;
      }
      area /= 2;
      if (Math.abs(area) < EPS) {
        const bb = zoneBounds(z);
        return { cx: (bb.minX + bb.maxX) / 2, cy: (bb.minY + bb.maxY) / 2 };
      }
      return { cx: cx / (6 * area), cy: cy / (6 * area) };
    }
  }
}

/**
 * Smallest distance from `(cx, cy)` to the zone boundary, used to
 * cap the radial-spread offset so shots stay inside their zone even
 * for thin or elongated shapes.
 */
function zoneInradius(z: TargetZone): number {
  switch (z.shape) {
    case 'circle':
      return z.geometry.r;
    case 'rect':
      return Math.min(z.geometry.w, z.geometry.h) / 2;
    case 'polygon': {
      // Approximation: half the smaller bounding-box dimension.
      // Good enough for the "fan shots out" use case.
      const b = zoneBounds(z);
      return Math.min(b.maxX - b.minX, b.maxY - b.minY) / 2;
    }
  }
}

function isPenaltyZone(z: TargetZone, hitZone: HitZone | undefined): boolean {
  if (hitZone && hitZone.valueNumeric < 0) return true;
  // 'NS' is the canonical IPSC no-shoot code; other admin-flagged
  // penalty zones are detected via negative `valueNumeric` above.
  if (z.zoneCode === 'NS') return true;
  return false;
}

function zoneFill(
  zoneCode: string,
  ord: number,
  hitZoneByCode: Map<string, HitZone>,
  geomColor: string | null | undefined,
): string {
  const hit = hitZoneByCode.get(zoneCode);
  const admin = hit?.displayColor ?? geomColor ?? null;
  if (admin) return admin;
  // Falls back to a neutral gradient based on ordering — innermost
  // (highest value) darkest, outermost lightest. Mirrors the look of
  // a printed target without baking in domain-specific colours.
  const palette = ['#15803d', '#65a30d', '#a16207', '#854d0e', '#3f3f46', '#27272a'];
  return palette[Math.min(ord, palette.length - 1)] ?? palette[palette.length - 1] ?? '#27272a';
}

function GenericShapeTarget({
  shots,
  shotCount,
  schemaKind,
  zones,
  targetZones,
  positionByShotId,
  pendingDetections,
  penalties,
  onConfirmDetection,
  onDismissDetection,
}: {
  shots: HitMarker[];
  shotCount: number;
  schemaKind: HitSchemaKind;
  zones: HitZone[];
  /** Pre-sorted inside-out (ordering ascending). */
  targetZones: TargetZone[];
  positionByShotId: Map<string, ShotPosition>;
  pendingDetections: PendingDetection[];
  penalties: PenaltyEntry[];
  onConfirmDetection: ((detectionId: string) => void) | undefined;
  onDismissDetection: ((detectionId: string) => void) | undefined;
}) {
  const hitZoneByCode = new Map(zones.map((z) => [z.zoneCode, z]));
  const targetZoneByCode = new Map(targetZones.map((z) => [z.zoneCode, z]));

  // Bounding box across all zones drives the SVG viewBox; pad
  // slightly so strokes and shot markers near the edge don't clip.
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const z of targetZones) {
    const b = zoneBounds(z);
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }
  const w = maxX - minX;
  const h = maxY - minY;
  const pad = Math.max(w, h) * 0.08;
  const vbX = minX - pad;
  const vbY = minY - pad;
  const vbW = w + pad * 2;
  // Reserve a strip below the target for the penalty badge row.
  const badgeH = penalties.length > 0 ? Math.max(w, h) * 0.07 : 0;
  const vbH = h + pad * 2 + (badgeH > 0 ? badgeH + pad * 0.5 : 0);

  // Marker size + stroke width scale with the target so they look
  // consistent regardless of whether geometry is in 0..1 or 0..200
  // units.
  const markerR = Math.max(w, h) * 0.045;
  const strokeW = Math.max(w, h) * 0.003;

  // Outer→inner so smaller (higher-value) zones draw on top.
  const outerToInner = [...targetZones].reverse();

  // Aspect-ratio-aware figure sizing: keep the long edge ≤ 16rem.
  const aspectClass = h > w ? 'max-w-[12rem]' : 'max-w-[16rem]';

  const ariaLabel = schemaKind === 'hit_factor' ? 'Trefferanzeige Silhouette' : 'Trefferanzeige';

  return (
    <figure className={`mx-auto w-full ${aspectClass}`}>
      <svg
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        className="w-full"
        role="img"
        aria-label={ariaLabel}
      >
        <PenaltyHatchDef />
        {outerToInner.map((z) => {
          const stroke = '#3f3f46';
          const fill = isPenaltyZone(z, hitZoneByCode.get(z.zoneCode))
            ? `url(#${PENALTY_HATCH_ID})`
            : zoneFill(z.zoneCode, z.ordering, hitZoneByCode, z.displayColor);
          if (z.shape === 'circle') {
            return (
              <circle
                key={`${z.zoneCode}-${z.ordering}`}
                cx={z.geometry.cx}
                cy={z.geometry.cy}
                r={z.geometry.r}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeW}
              />
            );
          }
          if (z.shape === 'rect') {
            return (
              <rect
                key={`${z.zoneCode}-${z.ordering}`}
                x={z.geometry.x}
                y={z.geometry.y}
                width={z.geometry.w}
                height={z.geometry.h}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeW}
              />
            );
          }
          // polygon
          const pts = z.geometry.points.map(([x, y]) => `${x},${y}`).join(' ');
          return (
            <polygon
              key={`${z.zoneCode}-${z.ordering}`}
              points={pts}
              fill={fill}
              stroke={stroke}
              strokeWidth={strokeW}
            />
          );
        })}

        {/* Pending CV detections — dashed translucent ghosts. */}
        {pendingDetections.map((d) => {
          const isPenalty = (d.valueNumeric ?? 0) < 0 || d.zoneCode === 'NS';
          const fill =
            schemaKind === 'hit_factor'
              ? ipscHoleFill(d.zoneCode, new Map(zones.map((z) => [z.zoneCode, z.displayColor])))
              : isPenalty
                ? '#ef4444'
                : holeFill(d.valueNumeric);
          const label = d.valueNumeric != null ? String(d.valueNumeric) : (d.zoneCode ?? '?');
          return (
            <PendingMarker
              key={d.id}
              cx={d.x}
              cy={d.y}
              r={markerR}
              fill={fill}
              strokeW={strokeW}
              fontSize={markerR * 1.05}
              label={label}
              onConfirm={onConfirmDetection ? () => onConfirmDetection(d.id) : undefined}
              onDismiss={onDismissDetection ? () => onDismissDetection(d.id) : undefined}
            />
          );
        })}

        {/* Bullet holes — numbered in shot order. Real coords win;
            otherwise fall back to centroid + per-zone fan. */}
        {shots.map((s) => {
          const realPos = positionByShotId.get(s.id);
          let cx: number;
          let cy: number;
          if (realPos) {
            cx = realPos.x;
            cy = realPos.y;
          } else {
            const tz = s.zoneCode ? targetZoneByCode.get(s.zoneCode) : undefined;
            // Fan only among shots in the same zone — a 5-shot round
            // with one A and four C should fan the C around the C
            // centroid, not around the whole circle.
            const peers = shots.filter(
              (p) => p.zoneCode === s.zoneCode && !positionByShotId.has(p.id),
            );
            const peerIdx = peers.findIndex((p) => p.id === s.id);
            const slots = Math.max(peers.length, 1);
            const angle = (peerIdx / slots) * 2 * Math.PI - Math.PI / 2;
            if (tz) {
              const c = zoneCentroid(tz);
              const inrad = zoneInradius(tz);
              const off = peers.length > 1 ? Math.max(inrad - markerR * 1.4, 0) * 0.6 : 0;
              cx = c.cx + off * Math.cos(angle);
              cy = c.cy + off * Math.sin(angle);
            } else {
              const slot = ((s.ord - 1) % Math.max(shotCount, 1)) / Math.max(shotCount, 1);
              cx = vbX + pad + slot * (vbW - pad * 2);
              cy = vbY + h + pad * 0.5;
            }
          }
          const tz = s.zoneCode ? targetZoneByCode.get(s.zoneCode) : undefined;
          const isPenalty =
            (s.valueNumeric != null && s.valueNumeric < 0) ||
            s.zoneCode === 'NS' ||
            (tz != null && isPenaltyZone(tz, hitZoneByCode.get(tz.zoneCode)));
          const fill = isPenalty
            ? '#ef4444'
            : schemaKind === 'hit_factor'
              ? ipscHoleFill(s.zoneCode, new Map(zones.map((z) => [z.zoneCode, z.displayColor])))
              : holeFill(s.valueNumeric);
          return (
            <g key={s.id}>
              <circle
                cx={cx}
                cy={cy}
                r={markerR}
                fill={fill}
                stroke="#0a0a0a"
                strokeWidth={strokeW * 3}
              />
              <text
                x={cx}
                y={cy + markerR * 0.06}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={markerR * 1.1}
                fontWeight={800}
                fill="#0a0a0a"
              >
                {isPenalty ? '!' : s.ord}
              </text>
            </g>
          );
        })}

        <PenaltyBadges
          penalties={penalties}
          x={vbX + pad}
          y={vbY + h + pad * 1.5}
          rowW={vbW - pad * 2}
          badgeH={badgeH}
          fontSize={badgeH * 0.55}
        />
      </svg>
      <figcaption className="sr-only">
        Treffer in Reihenfolge auf der Zielscheibe; Zahl auf der Markierung = Schussnummer.
      </figcaption>
    </figure>
  );
}
